/**
 * Memory Leak Detection and Prevention Utilities
 * Helps identify and prevent common memory leak patterns in React applications
 */

/**
 * Track active timers and intervals for cleanup
 */
class TimerTracker {
  private timers = new Set<NodeJS.Timeout>();
  private intervals = new Set<NodeJS.Timeout>();

  setTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const timer = setTimeout(() => {
      callback();
      this.timers.delete(timer);
    }, delay);
    this.timers.add(timer);
    return timer;
  }

  setInterval(callback: () => void, delay: number): NodeJS.Timeout {
    const interval = setInterval(callback, delay);
    this.intervals.add(interval);
    return interval;
  }

  clearTimeout(timer: NodeJS.Timeout): void {
    clearTimeout(timer);
    this.timers.delete(timer);
  }

  clearInterval(interval: NodeJS.Timeout): void {
    clearInterval(interval);
    this.intervals.delete(interval);
  }

  clearAll(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.intervals.forEach(interval => clearInterval(interval));
    this.timers.clear();
    this.intervals.clear();
  }

  getActiveCount(): { timers: number; intervals: number } {
    return {
      timers: this.timers.size,
      intervals: this.intervals.size,
    };
  }
}

/**
 * Track event listeners for cleanup
 */
class EventListenerTracker {
  private listeners = new Map<
    EventTarget,
    Array<{
      event: string;
      handler: EventListener;
      options?: AddEventListenerOptions;
    }>
  >();

  addEventListener(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    target.addEventListener(event, handler, options);

    if (!this.listeners.has(target)) {
      this.listeners.set(target, []);
    }
    const listenerEntry: {
      event: string;
      handler: EventListener;
      options?: AddEventListenerOptions;
    } = { event, handler };
    if (options !== undefined) {
      listenerEntry.options = options;
    }
    this.listeners.get(target)!.push(listenerEntry);
  }

  removeEventListener(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    target.removeEventListener(event, handler, options);

    const targetListeners = this.listeners.get(target);
    if (targetListeners) {
      const index = targetListeners.findIndex(
        l => l.event === event && l.handler === handler
      );
      if (index !== -1) {
        targetListeners.splice(index, 1);
      }
      if (targetListeners.length === 0) {
        this.listeners.delete(target);
      }
    }
  }

  removeAllListeners(): void {
    this.listeners.forEach((listeners, target) => {
      listeners.forEach(({ event, handler, options }) => {
        target.removeEventListener(event, handler, options);
      });
    });
    this.listeners.clear();
  }

  getActiveCount(): number {
    let count = 0;
    this.listeners.forEach(listeners => {
      count += listeners.length;
    });
    return count;
  }
}

/**
 * Component lifecycle tracker for memory leak detection
 */
export class ComponentLifecycleTracker {
  private timerTracker = new TimerTracker();
  private eventTracker = new EventListenerTracker();
  private abortControllers = new Set<AbortController>();
  private cleanupCallbacks = new Set<() => void>();

  // Timer methods
  setTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    return this.timerTracker.setTimeout(callback, delay);
  }

  setInterval(callback: () => void, delay: number): NodeJS.Timeout {
    return this.timerTracker.setInterval(callback, delay);
  }

  clearTimeout(timer: NodeJS.Timeout): void {
    this.timerTracker.clearTimeout(timer);
  }

  clearInterval(interval: NodeJS.Timeout): void {
    this.timerTracker.clearInterval(interval);
  }

  // Event listener methods
  addEventListener(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    this.eventTracker.addEventListener(target, event, handler, options);
  }

  removeEventListener(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    this.eventTracker.removeEventListener(target, event, handler, options);
  }

  // AbortController methods
  createAbortController(): AbortController {
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  abortAll(): void {
    this.abortControllers.forEach(controller => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    this.abortControllers.clear();
  }

  // Cleanup callback registration
  registerCleanup(callback: () => void): void {
    this.cleanupCallbacks.add(callback);
  }

  // Cleanup all resources
  cleanup(): void {
    this.timerTracker.clearAll();
    this.eventTracker.removeAllListeners();
    this.abortAll();
    this.cleanupCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in cleanup callback:', error);
      }
    });
    this.cleanupCallbacks.clear();
  }

  // Get statistics
  getStats(): {
    timers: number;
    intervals: number;
    eventListeners: number;
    abortControllers: number;
    cleanupCallbacks: number;
  } {
    const timerStats = this.timerTracker.getActiveCount();
    return {
      timers: timerStats.timers,
      intervals: timerStats.intervals,
      eventListeners: this.eventTracker.getActiveCount(),
      abortControllers: this.abortControllers.size,
      cleanupCallbacks: this.cleanupCallbacks.size,
    };
  }
}

/**
 * React hook for automatic resource cleanup
 */
import { useEffect, useRef } from 'react';

export function useLifecycleTracker(): ComponentLifecycleTracker {
  const trackerRef = useRef<ComponentLifecycleTracker | null>(null);

  if (!trackerRef.current) {
    trackerRef.current = new ComponentLifecycleTracker();
  }

  useEffect(() => {
    const tracker = trackerRef.current!;

    return () => {
      tracker.cleanup();
    };
  }, []);

  return trackerRef.current;
}

/**
 * Memory leak detection in development mode
 */
export class MemoryLeakDetector {
  private static instance: MemoryLeakDetector;
  private componentTrackers = new Map<string, ComponentLifecycleTracker>();
  private checkInterval: NodeJS.Timeout | null = null;

  private constructor() {
    if (import.meta.env.DEV) {
      this.startMonitoring();
    }
  }

  static getInstance(): MemoryLeakDetector {
    if (!MemoryLeakDetector.instance) {
      MemoryLeakDetector.instance = new MemoryLeakDetector();
    }
    return MemoryLeakDetector.instance;
  }

  registerComponent(componentId: string, tracker: ComponentLifecycleTracker): void {
    this.componentTrackers.set(componentId, tracker);
  }

  unregisterComponent(componentId: string): void {
    this.componentTrackers.delete(componentId);
  }

  private startMonitoring(): void {
    // Check for potential memory leaks every 30 seconds in development
    this.checkInterval = setInterval(() => {
      this.checkForLeaks();
    }, 30000);
  }

  private checkForLeaks(): void {
    const warnings: string[] = [];

    this.componentTrackers.forEach((tracker, componentId) => {
      const stats = tracker.getStats();
      const total =
        stats.timers +
        stats.intervals +
        stats.eventListeners +
        stats.abortControllers;

      // Warn if component has too many active resources
      if (total > 50) {
        warnings.push(
          `Component "${componentId}" has ${total} active resources (timers: ${stats.timers}, intervals: ${stats.intervals}, listeners: ${stats.eventListeners}, controllers: ${stats.abortControllers})`
        );
      }

      // Warn about long-running intervals
      if (stats.intervals > 10) {
        warnings.push(
          `Component "${componentId}" has ${stats.intervals} active intervals - potential memory leak`
        );
      }
    });

    if (warnings.length > 0) {
      console.warn('[Memory Leak Detection]', warnings.join('\n'));
    }
  }

  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  getReport(): {
    totalComponents: number;
    totalResources: number;
    componentDetails: Array<{
      id: string;
      stats: ReturnType<ComponentLifecycleTracker['getStats']>;
    }>;
  } {
    let totalResources = 0;
    const componentDetails: Array<{
      id: string;
      stats: ReturnType<ComponentLifecycleTracker['getStats']>;
    }> = [];

    this.componentTrackers.forEach((tracker, id) => {
      const stats = tracker.getStats();
      const resourceCount =
        stats.timers +
        stats.intervals +
        stats.eventListeners +
        stats.abortControllers;
      totalResources += resourceCount;
      componentDetails.push({ id, stats });
    });

    return {
      totalComponents: this.componentTrackers.size,
      totalResources,
      componentDetails,
    };
  }
}

/**
 * Hook for registering component with memory leak detector
 */
export function useMemoryLeakDetection(componentId: string): void {
  const tracker = useLifecycleTracker();

  useEffect(() => {
    if (import.meta.env.DEV) {
      const detector = MemoryLeakDetector.getInstance();
      detector.registerComponent(componentId, tracker);

      return () => {
        detector.unregisterComponent(componentId);
      };
    }
    return undefined;
  }, [componentId, tracker]);
}
